import { Component, EventEmitter, Input, Output } from '@angular/core';
import { DEFAULT_PAGE_SIZE, DEFAULT_PAGE_SIZE_OPTIONS } from './data-table.model';

/** Sentinel used in the page list to render a non-clickable ellipsis. */
export const ELLIPSIS = '…';

/**
 * Table footer: "Showing X to Y of Z records" on the left and a numbered
 * pager (Prev / 1 2 3 … N / Next) on the right.
 */
@Component({
  selector: 'app-table-pagination',
  templateUrl: './table-pagination.component.html',
  styleUrls: ['./table-pagination.component.css'],
  standalone: false
})
export class TablePaginationComponent {
  @Input() page: number = 1;
  @Input() pageSize: number = DEFAULT_PAGE_SIZE;
  @Input() total: number = 0;
  /** Choices offered by the page-size selector. */
  @Input() pageSizeOptions: number[] = DEFAULT_PAGE_SIZE_OPTIONS;
  /** Hides the page-size selector. Only set this where a screen genuinely must fix its page size. */
  @Input() showPageSizeSelector: boolean = true;

  @Output() pageChange: EventEmitter<number> = new EventEmitter<number>();
  /** Emitted with the newly chosen page size. The parent is responsible for resetting to page 1. */
  @Output() pageSizeChange: EventEmitter<number> = new EventEmitter<number>();

  readonly ellipsis = ELLIPSIS;

  /**
   * Options to render. The current page size is included even when it is not one of the configured
   * options, so a screen-specific size never shows the selector as blank.
   */
  get options(): number[] {
    const configured = this.pageSizeOptions || DEFAULT_PAGE_SIZE_OPTIONS;
    return configured.indexOf(this.pageSize) === -1
      ? configured.concat(this.pageSize).sort((a, b) => a - b)
      : configured;
  }

  /**
   * Handles a selection in the page-size dropdown.
   * @param value The selected value, as the raw string from the <select>.
   */
  onPageSizeChange(value: string): void {
    const size = Number(value);
    if (!size || size === this.pageSize) {
      return;
    }
    this.pageSizeChange.emit(size);
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.total / this.pageSize));
  }

  get start(): number {
    return this.total === 0 ? 0 : (this.page - 1) * this.pageSize + 1;
  }

  get end(): number {
    return Math.min(this.page * this.pageSize, this.total);
  }

  get pages(): Array<number | string> {
    return TablePaginationComponent.getPages(this.page, this.totalPages);
  }

  isNumber(value: number | string): boolean {
    return typeof value === 'number';
  }

  goTo(page: number | string): void {
    if (typeof page !== 'number') {
      return;
    }
    if (page < 1 || page > this.totalPages || page === this.page) {
      return;
    }
    this.pageChange.emit(page);
  }

  prev(): void {
    this.goTo(this.page - 1);
  }

  next(): void {
    this.goTo(this.page + 1);
  }

  /**
   * Builds the page list: always the first and last page, a ±1 window around
   * the current page, and an ellipsis bridging any gap. Pure logic (unit tested).
   * e.g. getPages(5, 9) -> [1, '…', 4, 5, 6, '…', 9]
   */
  static getPages(current: number, totalPages: number): Array<number | string> {
    if (totalPages <= 1) {
      return [1];
    }

    const wanted = new Set<number>([1, totalPages]);
    for (let page = current - 1; page <= current + 1; page++) {
      if (page >= 1 && page <= totalPages) {
        wanted.add(page);
      }
    }

    const sorted = Array.from(wanted).sort((a, b) => a - b);

    const result: Array<number | string> = [];
    let previous = 0;
    for (const page of sorted) {
      if (previous && page - previous > 1) {
        result.push(ELLIPSIS);
      }
      result.push(page);
      previous = page;
    }
    return result;
  }
}
